import {FAILURE, PENDING, SUCCESS} from '../../constants'
import {useReducer} from 'react'
import _ from 'lodash'
import moment from 'moment'

const ActionTypes = {
  FAILURE,
  PENDING,
  SUCCESS,
  RESET: 'RESET',
  SET_VIEW: 'SET_VIEW',
  REMOVE_EVENT_VIA_ID: 'REMOVE_EVENT_VIA_ID',
}
const InitialState = {
  viewData: {selectedView: null, selectedFilteredView: null},
  data: [],
  errors: null,
  isLoading: false,
  totals: {
    group_totals: 0,
    limit: 50,
    objects: 0,
    offset: 0,
  },
}

const Reducer = (state = InitialState, action) => {
  switch (action.type) {
    case ActionTypes.REMOVE_EVENT_VIA_ID:
      let _object = state.data.find(f => f.id === action.payload.entity_object_id)
      if (_object) {
        _object.events = _object.events.filter(f => f.id !== action.payload.id)
      }

      return {
        ...state,
        data: [...state.data],
      }
    case ActionTypes.SET_VIEW:
      return {
        ..._.cloneDeep(InitialState),
        viewData: action.payload.viewData,
      }
    case ActionTypes.RESET:
      return _.cloneDeep(InitialState)
    case PENDING:
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case SUCCESS:
      let _data = [...state.data]
      const data = processData(
        state.viewData,
        action.payload.objects,
        action.payload.entity_actions
      )

      // combine and append data
      if (_.isEqual(action.payload.view_data, state.viewData)) {
        _.each(data, d => {
          // find similar
          const same = _data.find(f => d.id === f.id)
          // if has same
          if (same) {
            // add non existing events
            const non_exist_events = _.differenceWith(
              d.events,
              same.events,
              (a, b) => a.id === b.id
            )
            if (non_exist_events.length > 0) {
              same.events = [...same.events, ...non_exist_events]
            }
          } else {
            _data.push(d)
          }
        })
      }

      const _totals = action.payload.events_only ? state.totals : action.payload.totals
      const _page = {
        limit: _totals?.limit,
        offset: _totals?.offset,
      }

      return {
        ...state,
        isLoading: false,
        errors: null,
        data: _data,
        totals: {...action.payload.totals, ..._page},
      }
    case FAILURE:
      return {
        ...state,
        errors: action.payload.errors,
        isLoading: false,
      }
    default:
      return state
  }
}

const useGanttResourceReducer = (state = InitialState) => {
  return useReducer(Reducer, state)
}

export default useGanttResourceReducer
export {ActionTypes, InitialState, Reducer}

const getGroupEvents = (d, viewData) => {
  const {selectedView, selectedFilteredView} = viewData
  const color = selectedFilteredView?.color || selectedView?.group_view?.color || 'green'
  return _.map(selectedView?.group_view?.groups, g => {
    if (!g.active) return null
    const _field = d.data[g.key] || null
    if (!_field) return null
    return createGroupDateField(g, color, d)
  }).filter(f => !!f)
}

const getFilteredViewColor = (data, colorViews) => {
  //console.log("data: ", data)
  let itemColor = ''
  _.each(colorViews, function(viewColor) {
    const colorFilters = viewColor?.filters || []
    _.each(colorFilters, function(filterItem) {
      _.each(filterItem?.values || [], function(filterValue) {
        if (data?.data) {
          const dataFilter = data?.data[filterItem.key]
          if (typeof dataFilter === 'object') {
            if (_.isEqual(dataFilter?.display_name, filterValue.value)) {
              itemColor = viewColor.color
            }
          } else {
            if (_.isEqual(dataFilter, filterValue.value)) {
              itemColor = viewColor.color
            }
          }
        }
      })
    })
  })
  if (_.isEmpty(itemColor)) {
    return colorViews[0]?.color
  } else {
    return itemColor
  }
}

const getActionEvents = (d, entityActions, viewData) => {
  const {selectedView, selectedFilteredViews} = viewData
  let color = selectedView?.group_view?.color || 'green'
  const events = _.map(d?.action_objects || [], abc => {
    //find entity_action base on abc id
    //check if date driven or standard
    //if date drive get start and end date (key_1 and key_2)
    //if standard find field key on entity_actions (first find the correct entity_action then find the field)
    if (!_.isEmpty(selectedFilteredViews)) {
      if (selectedFilteredViews.length > 1) {
        const colorResult = getFilteredViewColor(d, selectedFilteredViews)
        if (!_.isEmpty(colorResult)) {
          color = colorResult
        }
      } else {
        color = selectedFilteredViews[0]?.color || 'green'
      }
    }
    const action = _.find(entityActions, ['action_id', abc.entity_action_id])
    let data = []
    if (action !== undefined && action.type === 'ReservationAction') {
      data.push({
        type: action.name === 'System Reservation Action' ? 'SystemReservationAction' : action.type,
        reservation_uid: abc.reservation_uid,
        color: color,
        id: `${abc.id}_field_1_field_2`,
        start: abc?.values['field_1'],
        action_return_date: abc?.action_return_date // overdue
          ? moment(abc?.action_return_date).subtract({seconds: 1})
          : null,
        end: moment(abc?.values['field_2']).subtract({seconds: 1}),
        title:
          action.name === 'System Reservation Action'
            ? `Reservation #${abc.reservation_uid}`
            : action.name,
        entity_object: d,
        entity_key: d.entity.key,
        reserve_for: abc.reserve_for,
        raw: abc,
        action_values: abc.values,
        action: action,
        status: abc?.status,
        states: abc?.state,
      })
      return data
    }

    if (action !== undefined && action.name !== 'System Reservation Action') {
      let action_fields = _.map(action.fields, field => {
        if (
          _.includes(
            ['DateField', 'DateTimeField', 'DateStampField', 'DateTimeStampField', 'TimeField'],
            field.type
          )
        ) {
          if (
            action.type === 'ReservationAction' &&
            (field.key === 'field_1' || field.key === 'field_2')
          ) {
            return null
          }

          if (abc.values[field.key] !== undefined) {
            return createActionDateField(field, d, color, action, abc)
          } else {
            return null
          }
        }
      })
      _.map(action_fields, j => {
        if (j) {
          data.push(j)
        }
      })
    }
    return data
  })

  return _.flatten(events)
}

const processData = (viewData, itemListing, entityActions) => {
  const {selectedView, selectedFilteredViews} = viewData
  let color = selectedView?.group_view?.color || 'green'
  let _resources =
    !_.isEmpty(itemListing) &&
    itemListing.map(d => {
      // group events
      const _group_events = getGroupEvents(d, viewData)

      if (!_.isEmpty(selectedFilteredViews)) {
        if (selectedFilteredViews.length > 1) {
          if (!_.isEmpty(selectedFilteredViews)) {
            if (selectedFilteredViews.length > 1) {
              const colorResult = getFilteredViewColor(d, selectedFilteredViews)
              if (!_.isEmpty(colorResult)) {
                color = colorResult
              }
            } else {
              color = selectedFilteredViews[0]?.color || 'green'
            }
          }
        } else {
          color = selectedFilteredViews[0]?.color || 'green'
        }
      }

      // action events
      const _action_events = getActionEvents(d, entityActions, viewData)

      let _events = [..._group_events, ..._action_events]

      return {
        id: d.id,
        color: color,
        name: d.display_name,
        secondary_name: d.secondary_name,
        groupOnly: false,
        events: _events,
        disabled: d.is_disable,
      }
    })

  return _resources
}

const createGroupDateField = (group, color, entity_object) => {
  let event = {
    type: 'GroupDateField',
    raw: group,
    entity_object: entity_object,
    color: color,
    id: `${entity_object.id}_${group.key}`,
    start: moment(entity_object.data[group.key]).format('YYYY-MM-DD HH:mm:ss'),
    end: moment(entity_object.data[group.key])
      .set({hour: 23, minute: 59, second: 59, millisecond: 0})
      .format('YYYY-MM-DD HH:mm:ss'),
    title: `${entity_object.display_name}`,
    entity_key: entity_object.entity.key,
  }

  return event
}

const createActionDateField = (field, entity_object, color, action, action_object) => {
  let event = {
    type: 'ActionDateField',
    field: field,
    entity_object: entity_object,
    action: action,
    action_data: action_object,
    color: color,
    id: `${action_object.id}_${field.key}`,
    title: `${action.name}: ${field.name}`,
    entity_key: entity_object.entity.key,
  }

  switch (field.type) {
    case 'DateField':
      event = {
        ...event,
        start: moment(action_object.values[field.key])
          .set({hour: 0, minute: 0, second: 0, millisecond: 0})
          .format('YYYY-MM-DD HH:mm:ss'),
        end: moment(action_object.values[field.key])
          .set({hour: 23, minute: 59, second: 59, millisecond: 0})
          .format('YYYY-MM-DD HH:mm:ss'),
      }
      break
    default:
      event = {
        ...event,
        start: action_object.values[field.key],
        end: action_object.values[field.key],
      }
      break
  }

  return event
}
